import React, { useState, useEffect, useRef } from "react";
import { Input, Textarea } from "@chakra-ui/react";
import type { InputProps, TextareaProps } from "@chakra-ui/react";

interface DebouncedFieldProps {
  /** Current Formik field value — used to sync when value changes externally (form reset, enableReinitialize) */
  formikValue: string;
  /** Called on blur to sync the local value back to Formik */
  onSyncValue: (value: string) => void;
}

/**
 * Input that manages local state to prevent Formik re-render cascades on each keystroke.
 *
 * Problem: Formik re-renders its entire render-prop tree every time `handleChange` fires.
 * In forms with heavy children (user tables, condition builders, delivery config, etc.),
 * this causes severe typing lag.
 *
 * Solution: This component intercepts `onChange` and updates local state only.
 * The value is synced back to Formik on `blur`, which triggers a single re-render —
 * acceptable because blur is a one-time event, not a per-keystroke event.
 *
 * External sync: A ref tracks the last value we sent to Formik (via onSyncValue).
 * The useEffect only overwrites local state when formikValue diverges from that ref,
 * which means the change came from an external source (form reset, enableReinitialize,
 * step navigation) — not from our own pending local edits.
 */
export const DebouncedInput = React.memo(
  ({
    formikValue,
    onSyncValue,
    ...rest
  }: DebouncedFieldProps &
    Omit<InputProps, "value" | "onChange" | "onBlur">) => {
    const [localValue, setLocalValue] = useState(formikValue);
    const isDirtyRef = useRef(false);

    // Accept external value changes (form reset, enableReinitialize, step navigation)
    // ONLY when the user has no uncommitted local edits.
    useEffect(() => {
      if (!isDirtyRef.current) {
        setLocalValue(formikValue);
      }
    }, [formikValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalValue(e.target.value);
      isDirtyRef.current = true;
    };

    const handleBlur = () => {
      onSyncValue(localValue);
      isDirtyRef.current = false;
    };

    return (
      <Input
        {...rest}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  },
);

DebouncedInput.displayName = "DebouncedInput";

/**
 * Textarea variant of DebouncedInput — same optimization, different element.
 */
export const DebouncedTextarea = React.memo(
  ({
    formikValue,
    onSyncValue,
    ...rest
  }: DebouncedFieldProps &
    Omit<TextareaProps, "value" | "onChange" | "onBlur">) => {
    const [localValue, setLocalValue] = useState(formikValue);
    const isDirtyRef = useRef(false);

    useEffect(() => {
      if (!isDirtyRef.current) {
        setLocalValue(formikValue);
      }
    }, [formikValue]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setLocalValue(e.target.value);
      isDirtyRef.current = true;
    };

    const handleBlur = () => {
      onSyncValue(localValue);
      isDirtyRef.current = false;
    };

    return (
      <Textarea
        {...rest}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  },
);

DebouncedTextarea.displayName = "DebouncedTextarea";
